import { useDispatch } from 'react-redux';
import { setCaption } from '../slice/ImageAssetSlice';
import { persistAssetPatch } from './assetEditSeams';
import { useCaptionEditor } from './useCaptionEditor';
import { useCaptionSuggestions } from './useCaptionSuggestions';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';

interface CaptionEditorProps {
  photoUUID: string;
  assetUUID: string;
  caption: string;
  nextPhotoUUID?: string;
  prevPhotoUUID?: string;
}

/**
 * Inline caption editor rendered on the photo card's bottom gradient.
 * Ghost-text pattern: an underlay span renders the typed value transparently
 * plus the suggestion remainder in a muted tone; the transparent-background
 * input overlays it with identical font metrics, so the ghost appears to
 * continue the user's text.
 */
const CaptionEditor = ({
  photoUUID,
  assetUUID,
  caption,
  nextPhotoUUID,
  prevPhotoUUID
}: CaptionEditorProps) => {
  const dispatch = useDispatch();
  const suggestions = useCaptionSuggestions();

  const focusPhoto = (targetPhotoUUID?: string) => {
    if (!targetPhotoUUID) return;
    document
      .querySelector<HTMLInputElement>(
        `[data-caption-input="${targetPhotoUUID}"]`
      )
      ?.focus();
  };

  const { value, ghost, onChange, onKeyDown, onBlur } = useCaptionEditor({
    initial: caption,
    suggestions,
    onCommit: (next) => {
      const previous = caption;
      dispatch(setCaption({ photoUUID, caption: next }));
      // Staged photos are persisted Assets with real ids (commit flow and
      // staged uploads alike), so their captions save like any other photo.
      // The old DV-2252 `!staged` skip dated from optimistic-only fake ids and
      // silently dropped every staged caption on reload.
      persistAssetPatch({ assetUUID, caption: next }).catch(() => {
        dispatch(setCaption({ photoUUID, caption: previous }));
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Could not save the caption — it has been reset. Please try again.'
          })
        );
      });
    },
    onNext: () => focusPhoto(nextPhotoUUID),
    onPrev: () => focusPhoto(prevPhotoUUID)
  });

  return (
    <div className="relative min-w-0 flex-1">
      {/* overflow-hidden at both levels: a caption longer than the card (a
          real case at the phone grid's ~170px two-up cards) must not widen
          the card's scrollWidth through this positioned measurement span.
          The transparent copy keeps whitespace-pre (trailing spaces position
          the ghost) but clips with an invisible ellipsis once it exceeds the
          box — at that point the ghost is out of view anyway. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center overflow-hidden whitespace-pre px-2 text-base font-medium leading-6"
      >
        <span className="min-w-0 overflow-hidden text-ellipsis text-transparent">
          {value}
        </span>
        <span className="text-white/60">{ghost}</span>
      </span>
      {/* Touch tap floor (Unit 4 PR B, spec §2.5 correction 2026-08-21): the
          field is 24px tall by design — it sits on the card's 36px caption
          gradient, and growing it to 44px would cover 39% of a phone card's
          height. Instead a transparent ::before extends the HIT box to 44px
          (24 + 10 above + 10 below); pseudo-elements hit-test on behalf of
          their originating element, and expectTapTargets measures that
          enlargement (sonner's dismiss button floors its circle the same
          way). All four insets are set on purpose — the gate skips any pseudo
          carrying an `auto` inset, so inset-x-0 is load-bearing. So is
          `relative`: without it the pseudo resolves against the wrapper
          instead of the input, and the arithmetic would measure the wrong
          box. Coarse pointer only, so on desktop the 10px strip above the
          caption still opens the viewer. */}
      <input
        data-caption-input={photoUUID}
        aria-label={`Caption for ${caption || 'photo'}`}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder="Add a Caption"
        className="w-full bg-transparent px-2 text-base font-medium leading-6 text-white placeholder:text-white/50 drop-shadow-[1px_1px_3px_var(--color-shadow-text-on-image)] focus:outline-none focus-visible:ring-1 focus-visible:ring-white/70 rounded pointer-coarse:relative pointer-coarse:before:absolute pointer-coarse:before:inset-x-0 pointer-coarse:before:-inset-y-[10px] pointer-coarse:before:content-['']"
      />
    </div>
  );
};

export default CaptionEditor;
